import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMemo } from 'react';

interface ApproverProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route protection that checks if user has canApprove permission
 * Allows access to users with canApprove = true (regardless of role)
 */
const ApproverProtectedRoute = ({ children }: ApproverProtectedRouteProps) => {
  const { isAuthenticated, isLoading, user, getRoleBasedRedirect } = useAuth();

  // Memoize canApprove check to prevent unnecessary re-renders
  const canApprove = useMemo(() => {
    if (!user) return false;
    return user.can_approve || user.canApprove || false;
  }, [user]);

  /** Respect active profile (patient vs provider), not only account role */
  const redirectPath = useMemo(() => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin/dashboard';
    return getRoleBasedRedirect(user.role, user.active_profile_type);
  }, [user, getRoleBasedRedirect]);

  // Wait for auth to finish loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F7EC] to-[#E8F1D1] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#7E8D4C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6b6358]">Loading...</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check canApprove permission
  if (!canApprove) {
    return <Navigate to={redirectPath} replace />;
  }

  // User has permission, render children
  return <>{children}</>;
};

export default ApproverProtectedRoute;
